/** Peak / RMS envelopes for comparing original vs cleaned soundtracks. */

export function computeWaveformPeaks(samples, barCount = 160) {
  const data = samples && samples.length ? samples : [];
  const n = data.length;
  const bars = Math.max(8, Math.min(Number(barCount) || 160, Math.max(8, n || 8)));
  const peaks = new Array(bars).fill(0);
  const rms = new Array(bars).fill(0);
  if (!n) {
    return { peaks, rms, quietDb: null, peakDb: null };
  }
  for (let i = 0; i < bars; i += 1) {
    const start = Math.floor((i * n) / bars);
    const end = Math.max(start + 1, Math.floor(((i + 1) * n) / bars));
    const len = end - start;
    const step = Math.max(1, Math.floor(len / 80));
    let peak = 0;
    let sumSq = 0;
    let count = 0;
    for (let j = start; j < end; j += step) {
      const v = Number(data[j]) || 0;
      const a = Math.abs(v);
      if (a > peak) peak = a;
      sumSq += v * v;
      count += 1;
    }
    peaks[i] = peak;
    rms[i] = Math.sqrt(sumSq / Math.max(1, count));
  }
  const sorted = [...rms].sort((a, b) => a - b);
  const quiet = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.12))] || 0;
  const peak = Math.max(...peaks, 0);
  return {
    peaks,
    rms,
    quietDb: toDb(quiet),
    peakDb: toDb(peak),
  };
}

export function toDb(amplitude) {
  return 20 * Math.log10(Math.max(Number(amplitude) || 0, 1e-8));
}

export function waveformBarHeights(values, maxValue) {
  const list = Array.isArray(values) ? values : [];
  const ceiling = Math.max(Number(maxValue) || 0, ...list, 1e-6);
  return list.map((v) => Math.max(0.035, Math.min(1, (Number(v) || 0) / ceiling)));
}

/** dB scale so pause-floor drops stay visible after loudness matching. */
export function waveformBarHeightsDb(values, floorDb = -48) {
  const list = Array.isArray(values) ? values : [];
  const floor = Math.min(-12, Number(floorDb) || -48);
  const span = Math.max(12, 0 - floor);
  return list.map((v) => {
    const db = toDb(v);
    return Math.max(0.04, Math.min(1, (db - floor) / span));
  });
}

export function waveformRemovedRms(originalRms, cleanedRms) {
  const orig = Array.isArray(originalRms) ? originalRms : [];
  const cleaned = Array.isArray(cleanedRms) ? cleanedRms : [];
  const n = Math.min(orig.length, cleaned.length);
  const diffs = [];
  for (let i = 0; i < n; i += 1) {
    diffs.push(Math.max(0, (Number(orig[i]) || 0) - (Number(cleaned[i]) || 0)));
  }
  return diffs;
}

export function quietFloorDeltaDb(originalDb, cleanedDb) {
  if (!Number.isFinite(originalDb) || !Number.isFinite(cleanedDb)) return null;
  return originalDb - cleanedDb;
}
